import { useLocation } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { routes } from './app-routes';
import { routes as routesRevamp } from 'components/app/routes/app-routes-revamp';
import { useInitialState } from 'hooks/useInitialState';
import { checkMatchPath, stringToBool } from 'utils/misc';
import { useRevampJourney } from 'hooks/useRevampJourney';

import type { Route } from 'components/app/routes/app-routes';
import type { Route as RouteRevamp } from 'components/app/routes/app-routes-revamp';

const { MODE, VITE_ANY_PAGE_LANDING } = import.meta.env;

export type UseRoutesReturn = {
  isLoginEnabled: boolean;
  isLandingPage: boolean;
  routes: Route[];
  shouldShowPrompt: boolean;
  shouldRedirect: boolean;
  currentRoute?: Route | undefined;
};

export type UseRoutesReturnRevamp = {
  isLoginEnabled: boolean;
  isLandingPage: boolean;
  routes: RouteRevamp[];
  shouldShowPrompt: boolean;
  shouldRedirect: boolean;
  currentRoute?: RouteRevamp | undefined;
};

export const useRoutes = <
  T extends UseRoutesReturn | UseRoutesReturnRevamp
>(): T => {
  const location = useLocation();
  const flags = useFlags();
  const { hasStateInitialValue } = useInitialState();
  const { isRevampJourney } = useRevampJourney();
  const activeRoutes = isRevampJourney
    ? (routesRevamp as RouteRevamp[])
    : (routes as Route[]);
  const { pathname } = location;
  const currentRoute = activeRoutes.find((route) =>
    checkMatchPath(pathname, route.path)
  );
  const anyPageLanding = stringToBool(VITE_ANY_PAGE_LANDING);
  const isLoginEnabled = MODE !== 'test';
  const isLandingPage = !!currentRoute?.isLanding;
  const isEndingPage = !!currentRoute?.isEnding;
  /* istanbul ignore next -- @preserve */
  const shouldShowPrompt = !isLandingPage && !isEndingPage;
  /* istanbul ignore next -- @preserve */
  const shouldRedirect = !!(
    hasStateInitialValue &&
    !isLandingPage &&
    !anyPageLanding
  );

  const enabledRoutes = activeRoutes.filter((route) => {
    if (!route.featureFlag) {
      return true;
    }

    return flags[route.featureFlag];
  });

  return {
    isLoginEnabled,
    isLandingPage,
    routes: enabledRoutes,
    shouldShowPrompt,
    shouldRedirect,
    currentRoute
  } as T;
};
